/*
<summary>
   Single Mode "Keep Scan" icon button + popup. forceOpen (tray-transition gate) opens it
   EDITABLE - trash/keep-toggle/Save to choose which scanned tools carry into the next tray. A
   manual click opens the same-looking popup READ-ONLY (controls disabled, click-outside closes).
</summary>
<param>forceOpen: opens the popup automatically in editable mode.</param>
<param>onSave: called with the kept tool numbers (editable mode only).</param>
<param>onClose: called when the popup is dismissed without saving.</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { BookmarkCheckFill, Trash } from 'react-bootstrap-icons';
import { useStore } from '../../../contexts/store-provider';
import { formatMessage } from '../../../translations/format-message';

interface IProps {
  forceOpen?: boolean;
  onSave: (keptToolNumbers: string[]) => void;
  onClose?: () => void;
}

const KeepToolControl = ({ forceOpen = false, onSave, onClose }: IProps) => {
  const { toolBOMStore } = useStore();
  const { lastTrayScannedToolNumbers, scannedToolNumbers, scannedToolMainUnitNumbers } = toolBOMStore;

  const [showPopup, setShowPopup] = useState(false);
  const [keepFlags, setKeepFlags] = useState<Record<string, boolean>>({});
  // Tools removed from this list entirely (trash icon) - excluded from Save regardless of keep.
  const [removedTools, setRemovedTools] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Editable (keep/discard, mandatory) only during the real tray-transition gate; any other open
  // (manual button click) is a read-only check of what's scanned so far.
  const isEditable = forceOpen;
  const sourceTools = isEditable ? lastTrayScannedToolNumbers : scannedToolNumbers;

  useEffect(() => {
    if (forceOpen) {
      setShowPopup(true);
    }
  }, [forceOpen]);

  useEffect(() => {
    if (!showPopup) return;
    // Default every tool to "keep" whenever the popup opens for a fresh tool list.
    const initial: Record<string, boolean> = {};
    sourceTools.forEach((tool) => {
      initial[tool] = true;
    });
    setKeepFlags(initial);
    setRemovedTools([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup]);

  const visibleTools = sourceTools.filter((tool) => !removedTools.includes(tool));

  const toggleKeep = (tool: string) => {
    setKeepFlags((prev) => ({ ...prev, [tool]: !prev[tool] }));
  };

  const removeTool = (tool: string) => {
    setRemovedTools((prev) => [...prev, tool]);
  };

  const handleClose = () => {
    setShowPopup(false);
    onClose?.();
  };

  useEffect(() => {
    // Read-only mode is just a peek at the current scan list, so a click outside dismisses it
    // like any other dropdown. Editable mode is a mandatory gate (Save is the only way through)
    // and stays open until the operator resolves it.
    if (!showPopup || isEditable) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup, isEditable]);

  const handleSave = () => {
    const kept = visibleTools.filter((tool) => keepFlags[tool]);
    setShowPopup(false);
    onSave(kept);
  };

  return (
    <div className="position-relative d-inline-block me-2" ref={containerRef}>
      <ul className="add-list p-0 mb-0 justify-content-start">
        <li title={formatMessage('keep_scan')}>
          <a
            className="btn btn-light"
            onClick={() => setShowPopup(true)}
          >
            <BookmarkCheckFill size={18} />
          </a>
        </li>
      </ul>

      {showPopup && (
        <div
          className="dropdown-menu show p-3 shadow"
          style={{ minWidth: 320, top: '100%', left: 0 }}
        >
          <table className="table table-sm align-middle mb-2">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th><FormattedMessage id="used_tool" /></th>
                <th className="text-end"><FormattedMessage id="keep" /></th>
              </tr>
            </thead>
            <tbody>
              {visibleTools.length > 0 ? (
                visibleTools.map((tool) => (
                  <tr key={tool}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-icon rounded-circle text-danger bg-danger bg-opacity-10 border-0 d-inline-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32 }}
                        title={formatMessage('delete')}
                        disabled={!isEditable}
                        onClick={() => removeTool(tool)}
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                    <td>
                      {tool}
                      {scannedToolMainUnitNumbers.includes(tool) && ' (M)'}
                    </td>
                    <td className="text-end">
                      <Form.Check
                        type="switch"
                        id={`keep-tool-${tool}`}
                        checked={!!keepFlags[tool]}
                        disabled={!isEditable}
                        onChange={() => toggleKeep(tool)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    className="text-center text-muted"
                  >
                    <FormattedMessage id="no_tools_scanned" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-light btn-sm"
              title={formatMessage('close')}
              onClick={handleClose}
            >
              <FormattedMessage id="close" />
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!isEditable}
              onClick={handleSave}
            >
              <FormattedMessage id="save" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(KeepToolControl);
